import {
  Card,
  Column,
  Loader,
  Row,
  Text,
  useDesignTokens,
} from "@gradience/ui";
import Icon, { IconContainer } from "@gradience/ui/dist/components/icon";
import { useSelectedTest } from "../../../../domain/use-latest-test";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryOptions } from "../../../../lib/api";
import { useParams } from "@tanstack/react-router";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { getGroupQueryOptions } from "./utils";
import { KeyConceptResults } from "@gradience/api-types/src/group";

const columnHelper = createColumnHelper<KeyConceptResults>();

export function KeyConceptsTable() {
  const params = useParams({ from: "/$test-slug/groups/$id/" });
  const test = useSelectedTest();
  const concepts = useQuery(
    getQueryOptions({
      endpoint: "/groups/:id/key-concepts",
      pathParams: { id: params.id },
    })
  );
  const data = useMemo(
    () => (concepts.data?.concepts ? [...concepts.data.concepts] : []),
    [concepts.data?.concepts]
  );

  const designTokens = useDesignTokens();

  const [sorting, setSorting] = useState<SortingState>([]);

  const conceptTableColumns = useMemo(
    () => [
      columnHelper.accessor("name", {}),
      columnHelper.accessor("correct", {
        cell: (info) => {
          const row = info.row.original;
          const total = row.correct + row.incorrect + row.skipped;
          if (total === 0) return "—";
          const rate = row.correct / total;
          const label = `${Math.round(rate * 100)}%`;
          const [bg, color] =
            rate >= 0.75
              ? ["#d1fae5", "#065f46"]
              : rate >= 0.5
              ? ["#fef3c7", "#92400e"]
              : ["#fee2e2", "#991b1b"];
          return (
            <span
              style={{
                display: "inline-block",
                padding: "2px 10px",
                borderRadius: 99,
                backgroundColor: bg,
                color,
                fontWeight: 600,
              }}
            >
              {label}
            </span>
          );
        },
      }),
      columnHelper.accessor("incorrect", {
        cell: (info) => {
          const row = info.row.original;
          const total = row.correct + row.incorrect + row.skipped;
          return total === 0 ? "—" : `${Math.round((row.incorrect / total) * 100)}%`;
        },
      }),
      columnHelper.accessor("skipped", {
        cell: (info) => {
          const row = info.row.original;
          const total = row.correct + row.incorrect + row.skipped;
          return total === 0 ? "—" : `${Math.round((row.skipped / total) * 100)}%`;
        },
      }),
    ],
    []
  );

  const table = useReactTable({
    data,
    columns: conceptTableColumns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
    },
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
  });

  const group = useQuery(
    getGroupQueryOptions({ testId: test.data?.id, groupId: params.id })
  );

  return (
    <Card
      style={{
        flex: 1,
        padding: 24,
        gap: 24,
      }}
    >
      {group.isPending || concepts.isPending ? (
        <Loader />
      ) : (
        <>
          <Row
            gap={16}
            style={{
              alignItems: "center",
            }}
          >
            <IconContainer icon="file-branch" />
            <Column style={{ flex: 1 }}>
              <Text textStyle="20px - Bold">Key Concepts</Text>
              <Text textStyle="14px - Medium">
                See which concepts students in this group got correct,
                incorrect, or skipped.
              </Text>
            </Column>
          </Row>
          <table
            style={{
              borderCollapse: "collapse",
            }}
          >
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sortDir = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      style={{
                        padding: 16,
                        userSelect: "none",
                        cursor: canSort ? "pointer" : "default",
                      }}
                      // 4) toggle sorting on click
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      <Row gap={6} style={{ alignItems: "center" }}>
                        <Text
                          textStyle="subHeading"
                          style={{
                            textTransform: "uppercase",
                            textAlign: "start",
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </Text>
                        {sortDir === "asc" ? (
                          <Icon name="chevron-up-filled" />
                        ) : sortDir === "desc" ? (
                          <Icon name="chevron-down-filled" />
                        ) : null}
                      </Row>
                    </th>
                  );
                })}
              </tr>
            ))}
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        padding: "24px 16px",
                        borderTop: `1px solid ${designTokens.colors.border.subdued}`,
                      }}
                    >
                      <Text textStyle="14px - Medium">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Text>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
        </>
      )}
    </Card>
  );
}
